import clsx from 'clsx';
import type { CSSProperties, ReactNode } from 'react';
import { forwardRef } from 'react';
import useTestTag from '../../common/hooks/use-test-tag';
import { IconButton } from '../button/button';
import OdinIcon, { type OdinIconName } from '../odin-icon/odin-icon.component';

export interface NotificationProps {
  title?: string;
  description?: string;
  type?: 'success' | 'error' | 'warning' | 'info' | 'loading' | 'blank' | 'custom';
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  showCloseIcon?: boolean;
  onCloseClick?: () => void;
}

const Notification = forwardRef<HTMLDivElement, NotificationProps>(
  ({ title, description, type, children, style, className, showCloseIcon, onCloseClick }: NotificationProps, ref) => {
    const styles = clsx(
      `flex flex-nowrap rounded-lg px-4 py-3 text-mono-0`,
      {
        'bg-success': type === 'success',
        'bg-error': type === 'error',
        'bg-warning': type === 'warning',
        'bg-info': type === 'info' || type === 'custom' || type === 'loading',
      },
      className
    );

    const containerStyles = clsx('ml-3 text-mono-1 font-bold min-w-0 break-words', {
      'mr-6': showCloseIcon,
    });

    const icons: { icon: OdinIconName; type: string }[] = [
      {
        icon: 'CircleCheck',
        type: 'success',
      },
      {
        icon: 'OctagonAlert',
        type: 'error',
      },
      {
        icon: 'AlertCircle',
        type: 'warning',
      },
      {
        icon: 'Info',
        type: 'info',
      },
      {
        icon: 'Info',
        type: 'custom',
      },
      {
        icon: 'Loader',
        type: 'loading',
      },
    ];

    const icon = icons.find((item) => item.type === type);

    const handleCloseClick = () => {
      if (showCloseIcon && onCloseClick) {
        onCloseClick();
      }
    };

    const tagCreator = useTestTag('notification');

    return (
      <div className={styles} style={style} ref={ref}>
        <div className="mt-0.25" ref={tagCreator(type)}>
          {icon && <OdinIcon icon={icon.icon} spin={type === 'loading'} className="text-mono-1 font-bold" />}
        </div>
        <div className={containerStyles}>
          {title && <p className="font-bold">{title}</p>}
          {description && <p>{description}</p>}
          {children}
        </div>
        {showCloseIcon && <IconButton classNames="ml-auto text-mono-1 font-bold" onClick={handleCloseClick} icon="X" />}
      </div>
    );
  }
);

Notification.displayName = 'Notification';
export default Notification;
